// Opens the shared cross-app download modal, mirroring Match Tracker's own
// SessionCard download action (SessionCard/hooks.tsx's openDownloadModal):
// dispatches `toggleModal` with a `ChildComponent` that renders the shared
// `DownloadForm`, seeded with Reporting's own options/labels and a `Sport`
// bridged from the page's `ReportingSport`. For soccer, the form sits behind a
// General / Tracking tab split (packets hidden, tracking added); other sports
// keep the flat form unchanged.
import type { ReportingSport } from '@frontend/core'
import type { SessionDownloadOption } from '@frontend/core/lib/domains/sessions/download-definitions'
import { toggleModal } from '@frontend/core/lib/modules/modal/slice'
import { selectServerUrl } from '@frontend/core/lib/modules/servers/selectors'
import {
  DownloadForm,
  SessionDownloadTabs,
  useAppDispatch,
  useAppSelector,
  useSelectedOrganisationId
} from '@frontend/react'
import { getReportingSport } from './use-reporting-unit-system'
import {
  EXPORT_LABELS,
  EXPORT_OPTIONS,
  useSessionExport
} from './use-session-export'

interface UseExportModalArgs {
  sessionId: string
  sessionDate: string
  sessionName: string
  sport: ReportingSport
}

export function useExportModal({
  sessionId,
  sessionDate,
  sessionName,
  sport
}: UseExportModalArgs) {
  const dispatch = useAppDispatch()
  const { run } = useSessionExport({ sessionId, sessionDate, sessionName })
  const broadcastBaseUrl = useAppSelector(selectServerUrl('broadcastApi'))
  const organisationId = useSelectedOrganisationId()

  const reportingSport = getReportingSport(sport)
  // Soccer swaps `position_packets_parquet` for the per-team tracking surface,
  // so hide the packets option here; AMF/CFL keep it.
  const showTracking = reportingSport.props.supportsTrackingDownload ?? false
  const options = showTracking
    ? EXPORT_OPTIONS.filter((option) => option !== 'position_packets_parquet')
    : EXPORT_OPTIONS

  const openExportModal = () => {
    dispatch(
      toggleModal({
        active: true,
        className: 'modalSmall',
        ChildComponent: (props) => {
          const downloadForm = (
            <DownloadForm
              {...props}
              options={options}
              labels={EXPORT_LABELS}
              sport={reportingSport}
              cancelVariant='outline'
            />
          )

          if (!showTracking) return downloadForm

          return (
            <SessionDownloadTabs
              generalTab={downloadForm}
              tracking={{
                baseUrl: broadcastBaseUrl,
                sessionId,
                sessionDate,
                orgId: organisationId ?? undefined,
                onClose: () => dispatch(toggleModal({}))
              }}
            />
          )
        },
        handleProceed: (types: SessionDownloadOption[], unit: string) => {
          // Fires the downloads and closes immediately, without waiting for
          // them to settle — same invariant as Match Tracker's own
          // handleProceed (run() itself reports success/failure via toast).
          void run(types, unit)
          dispatch(toggleModal({}))
        },
        handleClose: () => dispatch(toggleModal({}))
      })
    )
  }

  return { openExportModal }
}
